var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useRef, useMemo } from 'react';
import Cookies from 'js-cookie';
/**
 * Custom hook to capture click outside by using Rect reference.
 *
 * usage:
 * Example 1. const ref = useOutsideClick(()=> {do something})
 * Example 2. const ref = useOutsideClick(useCallback(()=>{do something}, [deps] ) )
 * Use second example when the callback requires to be updated by changes in the React state.
 *
 * Ref usage: <div ref{ref} />
 * @param {function} callback - callback when click outside is triggered
 * @returns {React.Ref} ref
 */
export var useOutsideClick = function (callback) {
    var ref = useRef(null);
    useEffect(function () {
        var handleClick = function (event) {
            // @ts-ignore-next-line
            if (ref.current && !ref.current.contains(event.target)) {
                callback(event);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return function () {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [callback]);
    return ref;
};
// Checks for the presence of the 'UsableNetAssistive' cookie to determine if UsableNet is enabled
export var usableNetEnabled = Boolean(Cookies.get('UsableNetAssistive'));
/**
 * Fetches the current color details based on the selected color, variants, and sizes.
 *
 * @param {ProductColor | undefined} selectedColor - The currently selected color.
 * @param {Array<ProductVariant>} variants - The array of product variants.
 * @param {Array<ProductSizeGroup>} sizes - The array of product size groups.
 * @returns an object containing the current color variants, sizes, category, and price.
 */
export var getCurrentColorDetails = function (selectedColor, variants, sizes) {
    var _a, _b, _c, _d, _e, _f, _g;
    var currentColorVariants = selectedColor
        ? variants.filter(function (v) { var _a; return selectedColor.id === ((_a = v.color) === null || _a === void 0 ? void 0 : _a.id); })
        : variants;
    var currentColorSizes = useMemo(function () {
        var _a;
        return (_a = sizes[0]) === null || _a === void 0 ? void 0 : _a.options.map(function (option) {
            var _a;
            var matchingVariant = currentColorVariants.find(function (variant) { var _a, _b; return ((_b = (_a = variant.sizes) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id) === option.id; });
            if (matchingVariant) {
                return __assign(__assign({}, option), { available: matchingVariant.available, variantId: (_a = matchingVariant.externalSystems) === null || _a === void 0 ? void 0 : _a.shopify, itemCount: matchingVariant.itemCount });
            }
            return undefined;
        }).filter(function (x) { return Boolean(x); });
    }, [selectedColor]);
    var currentColorCategory = (_a = currentColorVariants.find(function (v) { return v.categories; })) === null || _a === void 0 ? void 0 : _a.categories;
    var currentColorPrice = (_b = currentColorVariants.find(function (v) { return v.price; })) === null || _b === void 0 ? void 0 : _b.price;
    // Grab first variant and extract data needed for QBS evaluation
    var currentColorQbsData = {
        component: ((_d = (_c = currentColorVariants[0]) === null || _c === void 0 ? void 0 : _c.components) === null || _d === void 0 ? void 0 : _d[0]) || {},
        categories: ((_e = currentColorVariants[0]) === null || _e === void 0 ? void 0 : _e.categories) || [],
        itemCount: ((_f = currentColorVariants[0]) === null || _f === void 0 ? void 0 : _f.itemCount) || 0,
        sku: ((_g = currentColorVariants[0]) === null || _g === void 0 ? void 0 : _g.sku) || ''
    };
    return {
        currentColorVariants: currentColorVariants,
        currentColorSizes: currentColorSizes,
        currentColorCategory: currentColorCategory,
        currentColorPrice: currentColorPrice,
        currentColorQbsData: currentColorQbsData
    };
};
